import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as myConstClass from '../constant.js';
import './WrittenReviewCard.css';
import quote from '../assets/images/quote.png';

const WrittenReviewCard = ({
  review,
  reviews = [],
  onReadAllReviews,
  onWriteReview,
  eventId,
  userId,
  onReviewSubmitted,
  className = '',
  hideWriteReview = false,
  ...rest
}) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newReview, setNewReview] = useState({
    text: '',
    author: '',
    location: '',
  });

  const handleToggleExpand = () => {
    setExpanded(!expanded);
    setShowWriteForm(false);
    // Optionally notify parent component
    if (onReadAllReviews && !expanded) {
      onReadAllReviews();
    }
  };

  const handleToggleWriteForm = () => {
    setShowWriteForm(!showWriteForm);
    // Optionally notify parent component
    if (onWriteReview && !showWriteForm) {
      onWriteReview();
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in (guest user check)
    const user_id = userId || localStorage.getItem('user_id');
    if (!user_id || user_id === '' || user_id === '0' || user_id === 0) {
      // Guest user - redirect to login
      // Store error message in localStorage - Signin page will show the toast
      localStorage.removeItem('error');
      localStorage.setItem('error', 'You must have login for submitting review.');
      // Store current path so Signin can return here after login
      localStorage.setItem('redirect_after_login', window.location.pathname);
      setTimeout(() => {
        history.push('/signin');
      }, 1000);
      return;
    }
    
    // Client-side validation
    const trimmedText = newReview.text.trim();
    const trimmedAuthor = newReview.author.trim();
    const trimmedLocation = newReview.location.trim();

    if (!trimmedAuthor) {
      toast.error('Name is required');
      return;
    }

    if (!trimmedText) {
      toast.error('Review description is required');
      return;
    }

    if (trimmedText.length < 10) {
      toast.error('Review must be at least 10 characters long');
      return;
    }

    if (trimmedText.length > 2000) {
      toast.error('Review must not exceed 2000 characters');
      return;
    }

    if (!eventId) {
      toast.error('Event ID is missing. Please refresh the page and try again.');
      return;
    }

    setSubmitting(true);

    try {
      const requestBody = {
        eventId: parseInt(eventId),
        name: trimmedAuthor,
        description: trimmedText,
      };

      // Add userId (user is already verified to be logged in above)
      const userIdNum = parseInt(user_id);
      requestBody.userId = userIdNum;

      // Add address if provided
      if (trimmedLocation) {
        requestBody.address = trimmedLocation;
      }

      const apiUrl = `${myConstClass.NODE_API_BASE}/reviews/add`;
      console.log('Submitting review to:', apiUrl);
      console.log('Request body:', requestBody);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        toast.error(`Server error: ${response.status} ${response.statusText}`);
        setSubmitting(false);
        return;
      }

      const data = await response.json();
      console.log('API Response data:', data);

      if (data.status === 'true') {
        toast.success(data.message || 'Review submitted successfully. It will be visible after moderation.');
        
        // Reset form
        setNewReview({ text: '', author: '', location: '' });
        setShowWriteForm(false);
        
        // Notify parent component to refresh reviews
        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
      } else {
        toast.error(data.message || 'Failed to submit review. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const allReviews = reviews && reviews.length > 0 ? reviews : (review ? [review] : []);
  const featuredReview = allReviews[0];
  const hasMoreReviews = allReviews.length > 1;

  return (
    <div className={`written-review-card ${expanded ? 'written-review-card--expanded' : ''} ${className}`.trim()} {...rest}>
      {!expanded && (
        <>
          <img className="written-review-card__quote-icon quote-icon" src={quote}/>
          {featuredReview ? (
            <>
              <p className="written-review-card__text">{featuredReview.text || featuredReview.review}</p>
              {featuredReview.author && (
                <p className="written-review-card__author">
                  - {featuredReview.author}
                  {featuredReview.location && `, ${featuredReview.location}`}
                </p>
              )}
            </>
          ) : (
            <p className="written-review-card__text">
              No reviews available yet. Be the first to share your experience!
            </p>
          )}
          
          <div className="written-review-card__actions">
            {hasMoreReviews && (
              <button
                type="button"
                className="written-review-card__read-all-btn"
                onClick={handleToggleExpand}
              >
                Read All Reviews
                <i className="fas fa-chevron-down" aria-hidden="true"></i>
              </button>
            )}
            {!hideWriteReview && (
              <button
                type="button"
                className="written-review-card__write-link"
                onClick={handleToggleWriteForm}
              >
                [Write a Review]
              </button>
            )}
          </div>
        </>
      )}

      {expanded && (
        <>
          <div className="written-review-card__header">
            <h3 className="written-review-card__title">All Reviews ({allReviews.length})</h3>
            <button
              type="button"
              className="written-review-card__close-btn"
              onClick={handleToggleExpand}
              aria-label="Close reviews"
            >
              <i className="fas fa-chevron-up" aria-hidden="true"></i>
            </button>
          </div>

          <div className="written-review-card__reviews-container">
            {allReviews.map((rev, index) => (
              <div key={index} className="written-review-card__review-item">
                <i className="fas fa-quote-left written-review-card__review-quote" aria-hidden="true"></i>
                <p className="written-review-card__review-text">{rev.text || rev.review}</p>
                {rev.author && (
                  <p className="written-review-card__review-author">
                    - {rev.author}
                    {rev.location && `, ${rev.location}`}
                  </p>
                )}
              </div>
            ))}
          </div>

          {!hideWriteReview && (
            <button
              type="button"
              className="written-review-card__write-review-btn"
              onClick={handleToggleWriteForm}
            >
              <i className="fas fa-pen" aria-hidden="true"></i>
              Write a Review
            </button>
          )}
        </>
      )}

      {showWriteForm && (
        <div className="written-review-card__write-form">
          <h4 className="written-review-card__form-title">Share Your Experience</h4>
          <form onSubmit={handleSubmitReview}>
            <div className="written-review-card__form-group">
              <label htmlFor="review-text">Your Review *</label>
              <textarea
                id="review-text"
                className="written-review-card__textarea"
                placeholder="Tell us about your experience... (minimum 10 characters)"
                rows="4"
                value={newReview.text}
                onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                maxLength={2000}
                required
                disabled={submitting}
              />
              <small className="written-review-card__char-count">
                {newReview.text.length} / 2000 characters
                {newReview.text.length > 0 && newReview.text.length < 10 && (
                  <span style={{ color: 'red', marginLeft: '8px' }}>
                    (Minimum 10 characters required)
                  </span>
                )}
              </small>
            </div>
            <div className="written-review-card__form-row">
              <div className="written-review-card__form-group">
                <label htmlFor="review-author">Your Name *</label>
                <input
                  type="text"
                  id="review-author"
                  className="written-review-card__input"
                  placeholder="Enter your name"
                  value={newReview.author}
                  onChange={(e) => setNewReview({ ...newReview, author: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="written-review-card__form-group">
                <label htmlFor="review-location">Location</label>
                <input
                  type="text"
                  id="review-location"
                  className="written-review-card__input"
                  placeholder="Your city"
                  value={newReview.location}
                  onChange={(e) => setNewReview({ ...newReview, location: e.target.value })}
                  disabled={submitting}
                />
              </div>
            </div>
            <div className="written-review-card__form-actions">
              <button
                type="submit"
                className="written-review-card__submit-btn"
                disabled={submitting || newReview.text.trim().length < 10 || newReview.text.trim().length > 2000 || !newReview.author.trim()}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button
                type="button"
                className="written-review-card__cancel-btn"
                onClick={handleToggleWriteForm}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

WrittenReviewCard.propTypes = {
  review: PropTypes.shape({
    text: PropTypes.string,
    review: PropTypes.string,
    author: PropTypes.string,
    location: PropTypes.string,
  }),
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      review: PropTypes.string,
      author: PropTypes.string,
      location: PropTypes.string,
    })
  ),
  onReadAllReviews: PropTypes.func,
  onWriteReview: PropTypes.func,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReviewSubmitted: PropTypes.func,
  className: PropTypes.string,
  hideWriteReview: PropTypes.bool,
};

WrittenReviewCard.defaultProps = {
  review: null,
  reviews: [],
  onReadAllReviews: () => {},
  onWriteReview: () => {},
  eventId: null,
  userId: null,
  onReviewSubmitted: () => {},
  className: '',
  hideWriteReview: false,
};

export default WrittenReviewCard;

